import * as React from "react"
import { cn } from "@/lib/utils"
import { 
  Sparkles, 
  Bot, 
  ChevronDown,
  Zap
} from "lucide-react"
import { useState, useEffect } from "react"

interface ChatMessagesContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
  isTyping?: boolean
  onScrollToBottom?: () => void
  hasNewMessages?: boolean
}

const ChatMessagesContainer = React.forwardRef<HTMLDivElement, ChatMessagesContainerProps>(
  ({ className, children, isTyping = false, onScrollToBottom, hasNewMessages, ...props }, ref) => {
    const [isScrolled, setIsScrolled] = useState(false)
    const [showScrollButton, setShowScrollButton] = useState(false)
    const [scrollProgress, setScrollProgress] = useState(0)

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
      const atBottom = scrollHeight - scrollTop - clientHeight < 100
      setIsScrolled(!atBottom)
      setShowScrollButton(!atBottom)
      
      // Calculate scroll progress
      const progress = (scrollTop / (scrollHeight - clientHeight)) * 100
      setScrollProgress(Math.min(100, Math.max(0, progress)))
    }

    // Auto scroll to bottom on new messages when near bottom
    useEffect(() => {
      if (hasNewMessages && scrollProgress > 80) {
        onScrollToBottom?.()
      }
    }, [hasNewMessages, scrollProgress, onScrollToBottom])

    return (
      <div className="relative h-full overflow-hidden bg-gradient-to-b from-white via-white to-slate-50/30">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-gradient-to-bl from-teal-100/5 to-cyan-100/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-gradient-to-tr from-purple-100/5 to-pink-100/5 rounded-full blur-3xl" />
          
          {/* Subtle Grid Pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.015)_1px,transparent_1px)] bg-[size:40px_40px]" />
          
          {/* Animated Gradient Orbs */}
          <div className="absolute top-1/4 left-1/4 w-48 h-48 bg-gradient-to-br from-teal-200/10 to-cyan-200/10 rounded-full animate-pulse" style={{ animationDuration: '8s' }} />
          <div className="absolute bottom-1/4 right-1/4 w-32 h-32 bg-gradient-to-tr from-purple-200/10 to-pink-200/10 rounded-full animate-pulse" style={{ animationDuration: '6s' }} />
        </div>

    

        {/* Scroll Progress Indicator */}
        {showScrollButton && (
          <div className="absolute top-16 left-0 right-0 h-1 z-20">
            <div 
              className="h-full bg-gradient-to-r from-teal-500/80 to-cyan-500/80 transition-all duration-300"
              style={{ width: `${scrollProgress}%` }}
            />
          </div>
        )}

        {/* Scroll to Bottom Button */}
        {showScrollButton && (
          <button
            onClick={onScrollToBottom}
            className="absolute bottom-6 right-6 z-20 p-3 bg-gradient-to-br from-white to-slate-50 border border-slate-200/50 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 active:scale-95 group animate-in slide-in-from-bottom-2"
          >
            <ChevronDown size={18} className="text-slate-600 group-hover:text-teal-600 transition-colors" />
          </button>
        )}

        {/* Messages Container */}
        <div
          ref={ref}
          className={cn(
            "relative h-full overflow-y-auto scroll-smooth",
            "scrollbar-thin scrollbar-thumb-slate-200/50 scrollbar-track-transparent",
            "hover:scrollbar-thumb-slate-300/60",
            "px-4 py-6 md:px-6",
            className
          )}
          onScroll={handleScroll}
          {...props}
        >
          {/* Welcome Header */}
          {/* <div className="mb-8">
            <div className="max-w-3xl mx-auto">
              <div className="bg-gradient-to-r from-white/50 to-slate-50/30 backdrop-blur-sm border border-slate-100/50 rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-teal-500/10 to-cyan-500/10 rounded-xl border border-teal-100/50">
                    <Zap className="h-6 w-6 text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-lg text-slate-900 mb-2">Welcome to Interactive Learning</h4>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      Start a conversation to explore topics with AI-powered explanations, 
                      interactive diagrams, and personalized learning paths.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="px-3 py-1.5 bg-teal-50 text-teal-700 text-xs font-medium rounded-full border border-teal-200">
                    Real-time explanations
                  </span>
                  <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                    Visual diagrams
                  </span>
                  <span className="px-3 py-1.5 bg-purple-50 text-purple-700 text-xs font-medium rounded-full border border-purple-200">
                    Step-by-step guidance
                  </span>
                </div>
              </div>
            </div>
          </div> */}

          {/* Messages Content */}
          <div className="space-y-4 max-w-3xl mx-auto">
            {children}
          </div>

          {/* Typing Indicator */}
          {isTyping && (
            <div className="max-w-3xl mx-auto mt-4">
              <div className="bg-gradient-to-br from-white to-slate-50/50 backdrop-blur-sm border border-slate-100/50 rounded-2xl p-4 shadow-sm animate-in slide-in-from-bottom-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-lg">
                    <Bot size={16} className="text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900 mb-1">AI is generating a response...</div>
                    <div className="text-xs text-slate-500">This may take a moment</div>
                  </div>
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* End of Conversation Indicator */}
          <div className="max-w-3xl mx-auto mt-8 pt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-100/50"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-4 py-1.5 bg-white text-slate-600 text-xs font-medium rounded-full border border-slate-200/50 backdrop-blur-sm">
                  End of conversation
                </span>
              </div>
            </div>
          </div>

          {/* Bottom Spacing */}
          <div className="h-8" />
        </div>
      </div>
    )
  },
)

ChatMessagesContainer.displayName = "ChatMessagesContainer"

export { ChatMessagesContainer }